import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { useAdmin } from '../../context/AdminContext';
import { getInitials } from '../../utils/helpers';
import './Header.css';

const Header = ({ isAdmin = false, sidebarCollapsed = false, onToggleSidebar }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showNumberSelector, setShowNumberSelector] = useState(false);
  const [notifications] = useState([
    {
      id: 1,
      type: 'success',
      title: 'Campaign Completed',
      message: 'Your marketing campaign has been sent successfully',
      time: '5 min ago',
      read: false
    },
    {
      id: 2,
      type: 'info',
      title: 'New Contact Added',
      message: '3 new contacts were imported to your list',
      time: '1 hour ago',
      read: false
    },
    {
      id: 3,
      type: 'warning',
      title: 'Low Wallet Balance',
      message: 'Your wallet balance is running low',
      time: '2 hours ago',
      read: true
    }
  ]);

  const { user, logout, switchWhatsAppAccount } = useAuth();
  const { admin, adminLogout } = useAdmin();
  const navigate = useNavigate();

  const userMenuRef = useRef(null);
  const notificationRef = useRef(null);
  const numberSelectorRef = useRef(null);

  const currentUser = isAdmin ? admin : user;
  const logoutFunction = isAdmin ? adminLogout : logout;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (numberSelectorRef.current && !numberSelectorRef.current.contains(event.target)) {
        setShowNumberSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSwitchAccount = async (whatsappNumber) => {
    try {
      if (whatsappNumber === user?.activeWhatsAppNumber) return;

      const response = await switchWhatsAppAccount(whatsappNumber);
      if (response.success) {
        toast.success(`Switched to ${whatsappNumber}`);
        setShowNumberSelector(false);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to switch account');
    }
  };

  const handleLogout = async () => {
    try {
      await logoutFunction();
      toast.success('Logged out successfully');
      navigate(isAdmin ? '/admin/login' : '/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const unreadNotifications = notifications.filter(n => !n.read).length;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return 'fas fa-check-circle';
      case 'warning': return 'fas fa-exclamation-triangle';
      case 'error': return 'fas fa-times-circle';
      default: return 'fas fa-info-circle';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'success': return '#28a745';
      case 'warning': return '#ffc107';
      case 'error': return '#dc3545';
      default: return '#007bff';
    }
  };

  return (
    <header className={`dashboard-header ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Left section */}
      <div className="header-left">
        <button className="mobile-menu-btn" onClick={onToggleSidebar}>
          <i className="fas fa-bars"></i>
        </button>

        <Link to={isAdmin ? "/admin/dashboard" : "/dashboard"} className="mobile-logo">
          <img src="/logo.svg" alt="MessagePe" height="24" />
        </Link>

        <div className="breadcrumb">
          <span className="breadcrumb-item">
            {isAdmin ? 'Admin Panel' : 'Dashboard'}
          </span>
        </div>
      </div>

      {/* Center section */}
      <div className="header-center">
        {/* <div className="search-box">
          <i className="fas fa-search search-icon"></i>
          <input
            type="text"
            placeholder={isAdmin ? "Search users, settings..." : "Search campaigns, contacts..."}
            className="search-input"
          />
        </div> */}
      </div>

      {/* Right section */}
      <div className="header-right">
        {/* Phone Number Selector */}
        {!isAdmin && user?.whatsappConnected && (
          <div className="header-item number-selector-dropdown" ref={numberSelectorRef}>
            <button
              className="number-selector-btn"
              onClick={() => setShowNumberSelector(!showNumberSelector)}
              title="Select WhatsApp Number"
            >
              <i className="fab fa-whatsapp"></i>
              <span className="active-number">{user?.activeWhatsAppNumber || user?.whatsappNumber}</span>
              {user?.whatsappAccounts?.length > 1 && (
                <i className="fas fa-chevron-down caret"></i>
              )}
            </button>

            {showNumberSelector && user?.whatsappAccounts?.length > 1 && (
              <div className="number-selector-menu">
                <div className="menu-header">
                  <span>Switch WhatsApp Account</span>
                </div>
                <div className="account-list">
                  {user.whatsappAccounts.map((account) => (
                    <button
                      key={account.whatsappNumber}
                      className={`account-item ${user.activeWhatsAppNumber === account.whatsappNumber ? 'active' : ''}`}
                      onClick={() => handleSwitchAccount(account.whatsappNumber)}
                    >
                      <div className="account-icon">
                        <i className="fab fa-whatsapp"></i>
                      </div>
                      <div className="account-info">
                        <span className="account-number">{account.whatsappNumber}</span>
                        {user.activeWhatsAppNumber === account.whatsappNumber && (
                          <span className="active-badge">Active</span>
                        )}
                      </div>
                      {user.activeWhatsAppNumber === account.whatsappNumber && (
                        <i className="fas fa-check check-mark"></i>
                      )}
                    </button>
                  ))}
                </div>
                {/* <div className="menu-footer">
                  <Link to="/connect-whatsapp" onClick={() => setShowNumberSelector(false)}>
                    <i className="fas fa-plus"></i>
                    <span>Connect New Number</span>
                  </Link>
                </div> */}
              </div>
            )}
          </div>
        )}

        {/* Notifications */}
        {/* <div className="header-item notification-dropdown" ref={notificationRef}>
          <button
            className="notification-btn"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <i className="fas fa-bell"></i>
            {unreadNotifications > 0 && (
              <span className="notification-badge">{unreadNotifications}</span>
            )}
          </button>

          {showNotifications && (
            <div className="notification-menu">
              <div className="notification-header">
                <h4>Notifications</h4>
                <button className="mark-all-read">Mark all read</button>
              </div>
              <div className="notification-list">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-item ${!notification.read ? 'unread' : ''}`}
                  >
                    <div
                      className="notification-icon"
                      style={{ color: getNotificationColor(notification.type) }}
                    >
                      <i className={getNotificationIcon(notification.type)}></i>
                    </div>
                    <div className="notification-content">
                      <h5>{notification.title}</h5>
                      <p>{notification.message}</p>
                      <span className="notification-time">{notification.time}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="notification-footer">
                <Link to="/notifications">View all notifications</Link>
              </div>
            </div>
          )}
        </div> */}

        {/* User Menu */}
        <div className="header-item user-dropdown" ref={userMenuRef}>
          <button
            className="user-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            {currentUser?.profilePicture ? (
              <img
                src={currentUser.profilePicture}
                alt={currentUser.name}
                className="user-avatar"
              />
            ) : (
              <div className="user-avatar initials">
                {getInitials(currentUser?.name)}
              </div>
            )}
            <div className="user-info">
              <span className="user-name">{currentUser?.name}</span>
              <span className="user-role">
                {isAdmin ? admin?.role?.replace('_', ' ') : 'User'}
              </span>
            </div>
            <i className="fas fa-chevron-down"></i>
          </button>

          {showUserMenu && (
            <div className="user-menu">
              <div className="user-menu-header">
                {currentUser?.profilePicture ? (
                  <img
                    src={currentUser.profilePicture}
                    alt={currentUser.name}
                    className="menu-avatar"
                  />
                ) : (
                  <div className="menu-avatar initials">
                    {getInitials(currentUser?.name)}
                  </div>
                )}
                <div className="menu-user-info">
                  <div className="menu-user-name">{currentUser?.name}</div>
                  <div className="menu-user-email">{currentUser?.email}</div>
                </div>
              </div>

              <div className="user-menu-items">
                <Link to="/settings" className="menu-item">
                  <i className="fas fa-user"></i>
                  <span>Profile Settings</span>
                </Link>

                {!isAdmin && (
                  <>
                    <Link to="/wallet" className="menu-item">
                      <i className="fas fa-credit-card"></i>
                      <span>Billing & Plans</span>
                    </Link>
                    <div className="menu-item wallet-info">
                      <i className="fas fa-wallet"></i>
                      <span>Wallet Balance: ₹{user?.walletBalance?.toFixed(2) || '0.00'}</span>
                    </div>
                  </>
                )}

                <a href="mailto:hey@vrittas.com" className="menu-item">
                  <i className="fas fa-question-circle"></i>
                  <span>Help & Support</span>
                </a>

                <div className="menu-divider"></div>

                <button className="menu-item logout-btn" onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt"></i>
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;